import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Select, Th, Td, parseDate, daysUntil } from './common';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'claimed', label: 'Claimed' },
  { value: 'unlocked', label: 'Unlocked' },
];

const BOOL_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const DEADLINE_WINDOWS = [
  { value: 'all', label: 'Any deadline' },
  { value: '3', label: 'Expires in 3 days' },
  { value: '7', label: 'Expires in 7 days' },
  { value: '14', label: 'Expires in 14 days' },
  { value: 'expired', label: 'Expired' },
];

const SORT_OPTIONS = [
  { value: 'claimed_desc', label: 'Claimed date (newest)' },
  { value: 'claimed_asc', label: 'Claimed date (oldest)' },
  { value: 'ref_desc', label: 'Referrals (high → low)' },
  { value: 'ref_asc', label: 'Referrals (low → high)' },
  { value: 'deadline_asc', label: 'Deadline (soonest)' },
  { value: 'deadline_desc', label: 'Deadline (latest)' },
];

export const AmbassadorsTab = ({ rows }) => {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [shared, setShared] = useState('all');
  const [verified, setVerified] = useState('all');
  const [proActive, setProActive] = useState('all');
  const [createdAsset, setCreatedAsset] = useState('all');
  const [deadlineWindow, setDeadlineWindow] = useState('all');
  const [minRefs, setMinRefs] = useState('');
  const [maxRefs, setMaxRefs] = useState('');
  const [claimedStart, setClaimedStart] = useState('');
  const [claimedEnd, setClaimedEnd] = useState('');
  const [eligibleOnly, setEligibleOnly] = useState(false);
  const [sort, setSort] = useState('deadline_asc');

  const filtered = useMemo(() => {
    const qNorm = q.trim().toLowerCase();
    const minR = minRefs === '' ? null : Number(minRefs);
    const maxR = maxRefs === '' ? null : Number(maxRefs);
    const start = claimedStart ? new Date(claimedStart + 'T00:00:00') : null;
    const end = claimedEnd ? new Date(claimedEnd + 'T23:59:59') : null;
    const dlWindow = deadlineWindow;

    const res = rows.filter((r) => {
      if (qNorm) {
        const hay = `${r.user_email || ''} ${r.user_name || ''}`.toLowerCase();
        if (!hay.includes(qNorm)) return false;
      }
      if (status !== 'all' && r.status !== status) return false;

      const matchBool = (opt, val) => {
        if (opt === 'all') return true;
        return opt === 'yes' ? !!val : !val;
      };
      if (!matchBool(shared, r.shared_social)) return false;
      if (!matchBool(verified, r.email_verified)) return false;
      if (!matchBool(proActive, r.ambassador_access_active)) return false;
      if (!matchBool(createdAsset, r.created_study_asset)) return false;

      const refs = r.referral_signups || 0;
      if (minR !== null && refs < minR) return false;
      if (maxR !== null && refs > maxR) return false;

      const claimedAt = parseDate(r.claimed_at);
      if (start && claimedAt && claimedAt < start) return false;
      if (end && claimedAt && claimedAt > end) return false;
      if ((start || end) && !claimedAt) return false;

      const dleft = daysUntil(r.task_deadline);
      if (dlWindow !== 'all') {
        if (dlWindow === 'expired') {
          if (dleft === null || dleft > 0) return false;
        } else {
          const n = Number(dlWindow);
          if (dleft === null || dleft <= 0 || dleft > n) return false;
        }
      }

      if (eligibleOnly) {
        if (r.status !== 'claimed') return false;
        if (dleft === null || dleft < 0) return false;
        if (!r.email_verified) return false;
        if (!r.created_study_asset) return false;
        if (!r.shared_social) return false;
        if ((r.referral_signups || 0) < 10) return false;
      }

      return true;
    });

    const sorters = {
      claimed_desc: (a, b) => (parseDate(b.claimed_at)?.getTime() || 0) - (parseDate(a.claimed_at)?.getTime() || 0),
      claimed_asc: (a, b) => (parseDate(a.claimed_at)?.getTime() || 0) - (parseDate(b.claimed_at)?.getTime() || 0),
      ref_desc: (a, b) => (b.referral_signups || 0) - (a.referral_signups || 0),
      ref_asc: (a, b) => (a.referral_signups || 0) - (b.referral_signups || 0),
      deadline_asc: (a, b) => (daysUntil(a.task_deadline) ?? 999999) - (daysUntil(b.task_deadline) ?? 999999),
      deadline_desc: (a, b) => (daysUntil(b.task_deadline) ?? 999999) - (daysUntil(a.task_deadline) ?? 999999),
    };

    return res.sort(sorters[sort] || sorters.deadline_asc);
  }, [rows, q, status, shared, verified, proActive, createdAsset, deadlineWindow, minRefs, maxRefs, claimedStart, claimedEnd, eligibleOnly, sort]);

  return (
    <>
      <div className=" border border-[var(--isp-glass-border)] bg-slate-900/60 backdrop-blur-xl p-5">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <Select label="Status" value={status} onChange={setStatus} options={STATUS_OPTIONS} />
          <Select label="Shared social" value={shared} onChange={setShared} options={BOOL_OPTIONS} />
          <Select label="Email verified" value={verified} onChange={setVerified} options={BOOL_OPTIONS} />
          <Select label="Pro active" value={proActive} onChange={setProActive} options={BOOL_OPTIONS} />
          <Select label="Created study asset" value={createdAsset} onChange={setCreatedAsset} options={BOOL_OPTIONS} />
          <Select label="Deadline window" value={deadlineWindow} onChange={setDeadlineWindow} options={DEADLINE_WINDOWS} />

          <div>
            <label className="text-xs text-[var(--isp-body)]">Min referrals</label>
            <input
              type="number"
              value={minRefs}
              onChange={(e) => setMinRefs(e.target.value)}
              className="mt-1 w-full rounded-xl bg-slate-950/40 border border-[var(--isp-glass-border)] px-3 py-2 text-sm text-[var(--isp-body)] outline-none"
              placeholder="0"
            />
          </div>
          <div>
            <label className="text-xs text-[var(--isp-body)]">Max referrals</label>
            <input
              type="number"
              value={maxRefs}
              onChange={(e) => setMaxRefs(e.target.value)}
              className="mt-1 w-full rounded-xl bg-slate-950/40 border border-[var(--isp-glass-border)] px-3 py-2 text-sm text-[var(--isp-body)] outline-none"
              placeholder="100"
            />
          </div>
          <div>
            <label className="text-xs text-[var(--isp-body)]">Claimed start</label>
            <input
              type="date"
              value={claimedStart}
              onChange={(e) => setClaimedStart(e.target.value)}
              className="mt-1 w-full rounded-xl bg-slate-950/40 border border-[var(--isp-glass-border)] px-3 py-2 text-sm text-[var(--isp-body)] outline-none"
            />
          </div>
          <div>
            <label className="text-xs text-[var(--isp-body)]">Claimed end</label>
            <input
              type="date"
              value={claimedEnd}
              onChange={(e) => setClaimedEnd(e.target.value)}
              className="mt-1 w-full rounded-xl bg-slate-950/40 border border-[var(--isp-glass-border)] px-3 py-2 text-sm text-[var(--isp-body)] outline-none"
            />
          </div>
          <Select label="Sort" value={sort} onChange={setSort} options={SORT_OPTIONS} />
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-[var(--isp-body)] select-none">
              <input
                type="checkbox"
                checked={eligibleOnly}
                onChange={(e) => setEligibleOnly(e.target.checked)}
              />
              Eligible to unlock only
            </label>
          </div>
          <div className="lg:col-span-2">
            <label className="text-xs text-[var(--isp-body)]">Search (email or name)</label>
            <div className="mt-1 flex items-center gap-2 rounded-xl bg-slate-950/40 border border-[var(--isp-glass-border)] px-3 py-2">
              <Search className="w-4 h-4 text-[var(--isp-muted)]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="e.g. student@uni.edu"
                className="w-full bg-transparent outline-none text-sm text-[var(--isp-body)] placeholder:text-[var(--isp-muted)]"
              />
            </div>
          </div>
        </div>

        <div className="mt-4 text-sm text-[var(--isp-body)]">
          Showing <span className="font-semibold text-white">{filtered.length}</span> records
        </div>
      </div>

      <div className="mt-6  border border-[var(--isp-glass-border)] bg-slate-900/60 backdrop-blur-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-950/40 text-[var(--isp-body)]">
              <tr>
                <Th>Email</Th>
                <Th>Name</Th>
                <Th>Status</Th>
                <Th>Verified</Th>
                <Th>Pro active</Th>
                <Th>Shared</Th>
                <Th>Study asset</Th>
                <Th>Referrals</Th>
                <Th>Deadline (days)</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const dleft = daysUntil(r.task_deadline);
                const statusCls = r.status === 'unlocked'
                  ? 'bg-green-500/10 text-green-400 border-green-500/20'
                  : 'bg-slate-800/40 text-slate-200 border-[var(--isp-glass-border)]';
                return (
                  <tr key={r.user_id} className="border-t border-[var(--isp-glass-border)] hover:bg-[var(--isp-glass-inner)]">
                    <Td>{r.user_email}</Td>
                    <Td>{r.user_name || '—'}</Td>
                    <Td>
                      <span className={`text-xs font-semibold px-2 py-1 rounded-lg border ${statusCls}`}>
                        {r.status}
                      </span>
                    </Td>
                    <Td>{r.email_verified ? 'Yes' : 'No'}</Td>
                    <Td>{r.ambassador_access_active ? 'Yes' : 'No'}</Td>
                    <Td>{r.shared_social ? 'Yes' : 'No'}</Td>
                    <Td>{r.created_study_asset ? 'Yes' : 'No'}</Td>
                    <Td className="font-semibold text-white">{r.referral_signups || 0}</Td>
                    <Td>{dleft === null ? '—' : dleft}</Td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-10 text-[var(--isp-body)]">No results</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
